import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { BaseCreatorDto, TrackMinDto } from "@/lib/lutely-api/gen/dtos";
import type {
  TracksControllerTrackCreateType,
  TracksControllerTracksListType,
} from "@/lib/lutely-api/gen/dtos";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import AudiolabContext from "./AudiolabContext";
import type {
  AudiolabContextType,
  ProcessingTrack,
  ProcessingTrackStatus,
  UploadQueueItem,
} from "./AudiolabContext";
import type { TrackAssetStatusSummaryDto } from "@/lib/lutely-api/gen/dtos";

import { useCollapseBannerOnUpload } from "@/components/account/onboarding/useCollapseBannerOnUpload";
import UploadWizardModal from "@/components/audiolab/UploadWizardModal";
import {
  MAX_TRACK_UPLOAD_BATCH,
  TRACK_UPLOAD_CONCURRENCY,
} from "@/lib/config/constants.config";
import { generateInventory } from "@/lib/generateInventory";
import { trackAssetsControllerTrackAssetsGetStatus } from "@/lib/lutely-api/gen/track-assets/track-assets";
import {
  getErrorMessage,
  getErrorStatus,
} from "@/lib/lutely-api/error-manager";
import { trackProcessControllerTrackProcessOngoing } from "@/lib/lutely-api/gen/track-processes/track-processes";
import {
  getTracksControllerUploadCountQueryKey,
  getTracksControllerUploadQuotaQueryKey,
  tracksControllerPresignUpload,
  tracksControllerTrackCreate,
} from "@/lib/lutely-api/gen/tracks/tracks";
import { saveTrackUploadStartedAt } from "@/lib/storage-utils";
import { uploadToS3WithProgress } from "@/lib/upload";
import { isValidAudioFileType, isValidFileSize, isZip } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

type AudiolabProviderProps = {
  children: ReactNode;
};

const MAX_STATUS_POLL_FAILURES = 3;

function deriveProcessingStatus(
  summary: TrackAssetStatusSummaryDto,
): ProcessingTrackStatus {
  if (summary.completionPercentage >= 100) return "done";
  if (summary.completionPercentage > 0) return "generating";
  return "pending";
}

export default function AudiolabProvider({ children }: AudiolabProviderProps) {
  const { data: user, isLabelSubscriber } = useLoggedInUser();
  const creators = user?.creators ?? [];

  // ── Creator selection state ──────────────────────────────────────────

  const defaultCreator = useMemo(
    () => creators.find((c) => c.isDefault === true) ?? creators[0],
    [creators],
  );

  const [selectedCreator, setSelectedCreatorState] = useState<
    BaseCreatorDto | undefined
  >(undefined);

  const resolvedCreator = selectedCreator ?? defaultCreator;

  // A label's main artist is the account itself, so working as them means
  // seeing the whole roster rather than their own content alone.
  const viewAllArtists =
    creators.length >= 2 && resolvedCreator?.isDefault === true;

  const setSelectedCreator = useCallback((creator: BaseCreatorDto) => {
    setSelectedCreatorState(creator);
  }, []);

  const isReadOnly = useMemo(() => {
    if (!resolvedCreator) return false;
    const isDefault = resolvedCreator.isDefault;
    return isDefault === false && !isLabelSubscriber;
  }, [resolvedCreator, isLabelSubscriber]);

  // ── Track upload / processing state ──────────────────────────────────

  const [processingTracks, setProcessingTracks] = useState<ProcessingTrack[]>(
    [],
  );
  const [showUpload, setShowUploadState] = useState(false);
  // Non-null only while an album flow owns the wizard.
  const [lockedUploadCreatorId, setLockedUploadCreatorId] = useState<
    string | null
  >(null);

  // True only while the wizard was entered through "Master track".
  const [uploadMasteringPreset, setUploadMasteringPreset] = useState(false);

  // Release-page cap on the batch (a Single takes one track).
  const [uploadMaxFiles, setUploadMaxFiles] = useState<number | null>(null);

  // One-shot: a release page waiting to attach the next created batch.
  const uploadCreatedCallbackRef = useRef<
    ((tracks: TrackMinDto[]) => void) | null
  >(null);

  // The wizard uploads a batch (up to MAX_TRACK_UPLOAD_BATCH files), so upload
  // state is per file rather than a single progress number.
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isUploading = uploadQueue.some(
    (item) =>
      item.status === "pending" ||
      item.status === "uploading" ||
      item.status === "creating",
  );

  const setShowUpload = useCallback((visible: boolean) => {
    setShowUploadState(visible);
    // Closing always drops both presets, so the next ordinary upload gets the
    // artist selector and the mastering question back. The finished batch goes
    // with it — the wizard should never reopen showing the last upload.
    if (!visible) {
      setLockedUploadCreatorId(null);
      setUploadMasteringPreset(false);
      setUploadMaxFiles(null);
      uploadCreatedCallbackRef.current = null;
      setUploadQueue([]);
      setErrorMessage(undefined);
    }
  }, []);

  const openUploadForMastering = useCallback(() => {
    setUploadMasteringPreset(true);
    setShowUploadState(true);
  }, []);

  const openUploadForCreator = useCallback((creatorId: string) => {
    setLockedUploadCreatorId(creatorId);
    setShowUploadState(true);
  }, []);

  // Release-page uploads: pinned artist, optional Master-track preset, and a
  // callback so the caller can attach the new track to its release.
  const openUploadForRelease = useCallback(
    (
      creatorId: string,
      options?: {
        mastering?: boolean;
        maxFiles?: number;
        onCreated?: (tracks: TrackMinDto[]) => void;
      },
    ) => {
      setLockedUploadCreatorId(creatorId);
      setUploadMasteringPreset(options?.mastering ?? false);
      setUploadMaxFiles(options?.maxFiles ?? null);
      uploadCreatedCallbackRef.current = options?.onCreated ?? null;
      setShowUploadState(true);
    },
    [],
  );

  const queryClient = useQueryClient();
  const collapseBannerIfExpanded = useCollapseBannerOnUpload();

  // Consecutive status-poll failures per track. The poll below runs every 3s
  // for as long as a track is not "done", so a request that always fails would
  // otherwise repeat forever — and because this call is outside React Query,
  // the client's retry policy does not apply to it.
  const statusFailuresRef = useRef<Map<string, number>>(new Map());

  const getStatusForTrack = useCallback((trackId: string) => {
    trackAssetsControllerTrackAssetsGetStatus({ trackId })
      .then((res) => {
        statusFailuresRef.current.delete(trackId);
        const status = deriveProcessingStatus(res);
        setProcessingTracks((prev) =>
          prev.map((track) =>
            track.trackId === trackId
              ? {
                  ...track,
                  completionPercentage: res.completionPercentage,
                  status,
                }
              : track,
          ),
        );
      })
      .catch((error: unknown) => {
        const status = getErrorStatus(error);
        const failures = (statusFailuresRef.current.get(trackId) ?? 0) + 1;
        statusFailuresRef.current.set(trackId, failures);

        // 401/403 is terminal — this session cannot read the track's status, so
        // no amount of polling will change the answer. Anything else gets a few
        // attempts in case it is transient. Either way, stop tracking the track
        // rather than leaving it to spin in the activity bar indefinitely.
        if (
          status === 401 ||
          status === 403 ||
          failures >= MAX_STATUS_POLL_FAILURES
        ) {
          statusFailuresRef.current.delete(trackId);
          setProcessingTracks((prev) =>
            prev.filter((track) => track.trackId !== trackId),
          );
        }
      });
  }, []);

  const addTrack = useCallback((track: TrackMinDto) => {
    // Newest upload first — it surfaces at the top of the activity list.
    setProcessingTracks((prev) => [
      { ...track, completionPercentage: 0, status: "generating" },
      ...prev.filter((t) => t.trackId !== track.trackId),
    ]);
  }, []);

  const removeTrack = useCallback((id: string) => {
    setProcessingTracks((prev) => prev.filter((track) => track.trackId !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setProcessingTracks((prev) => prev.filter((t) => t.status !== "done"));
  }, []);

  const updateQueueItem = useCallback(
    (id: string, patch: Partial<UploadQueueItem>) => {
      setUploadQueue((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  // One file, from presign through to its created track. Resolves with the
  // track on success and null on failure — a batch keeps going either way, so a
  // single bad file can't take the other nineteen down with it.
  const uploadQueueItem = useCallback(
    async (
      item: UploadQueueItem,
      type: TracksControllerTrackCreateType,
      creatorId: string,
      shouldMaster: boolean,
    ): Promise<TrackMinDto | null> => {
      try {
        let inventory: Record<string, any> | undefined;
        if (isZip(item.file)) {
          inventory = await generateInventory(item.file);
        }

        updateQueueItem(item.id, { status: "uploading", progress: 0 });

        const presignResponse = await tracksControllerPresignUpload({
          type,
          creatorId,
          filename: item.file.name,
        });

        await uploadToS3WithProgress(presignResponse.presignedUrl, item.file, {
          onProgress: (progress) => updateQueueItem(item.id, { progress }),
        });

        updateQueueItem(item.id, { progress: 100, status: "creating" });

        const track = await tracksControllerTrackCreate(
          { file: undefined as any },
          {
            s3Url: presignResponse.s3Url,
            type,
            creatorId,
            inventory: inventory ? JSON.stringify(inventory) : undefined,
            alreadyMastered: shouldMaster === false ? true : undefined,
          },
        );

        updateQueueItem(item.id, { status: "done" });

        // t0 for the time-to-publish metric (LUT-775), consumed on publish.
        saveTrackUploadStartedAt(track.trackId);
        addTrack(track);

        return track;
      } catch (error: unknown) {
        console.error("Upload error:", error);
        updateQueueItem(item.id, {
          status: "error",
          errorMessage: getErrorMessage(error) || "Upload failed",
        });
        return null;
      }
    },
    [addTrack, updateQueueItem],
  );

  const createTracks = useCallback(
    async (
      files: File[],
      type: TracksControllerTrackCreateType,
      creatorId: string,
      shouldMaster: boolean,
    ) => {
      const items: UploadQueueItem[] = files
        .slice(0, MAX_TRACK_UPLOAD_BATCH)
        .map((file) => ({
          id: crypto.randomUUID(),
          file,
          progress: 0,
          status: "pending",
        }));

      if (items.length === 0) return;

      // A new batch replaces the previous one, so a retry after a failure
      // starts from a clean list.
      setUploadQueue(items);

      // A few at a time: 20 parallel PUTs of up to 200MB starve each other and
      // make every progress bar crawl, while one at a time wastes bandwidth.
      const created: TrackMinDto[] = [];
      let cursor = 0;
      const worker = async () => {
        while (cursor < items.length) {
          const item = items[cursor];
          cursor += 1;
          const track = await uploadQueueItem(
            item,
            type,
            creatorId,
            shouldMaster,
          );
          if (track) created.push(track);
        }
      };

      await Promise.all(
        Array.from(
          { length: Math.min(TRACK_UPLOAD_CONCURRENCY, items.length) },
          worker,
        ),
      );

      const failedCount = items.length - created.length;

      if (created.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["/tracks"] });
        queryClient.invalidateQueries({
          queryKey: getTracksControllerUploadCountQueryKey(),
        });
        // Free-tier uploads are metered, so the wizard's remaining count has to
        // reflect the batch that just landed.
        queryClient.invalidateQueries({
          queryKey: getTracksControllerUploadQuotaQueryKey(),
        });

        toast.success(
          created.length === 1
            ? "Upload complete. Your assets are generating."
            : `${created.length} uploads complete. Your assets are generating.`,
        );

        collapseBannerIfExpanded();

        // Kept for the next batch rather than consumed here: a batch that
        // partly failed leaves the wizard open, and the retry's tracks belong
        // on the release just as much as this batch's did. Each call carries
        // only its own batch, so nothing is attached twice, and closing the
        // wizard clears the ref.
        uploadCreatedCallbackRef.current?.(created);
      }

      if (failedCount > 0) {
        toast.error(
          failedCount === 1
            ? "1 file failed to upload."
            : `${failedCount} files failed to upload.`,
        );
        // Leave the wizard open on the failed rows: closing it would hide the
        // only place that says which files didn't make it.
        return;
      }

      setUploadQueue([]);
      setErrorMessage(undefined);
      setShowUpload(false);
    },
    [collapseBannerIfExpanded, queryClient, setShowUpload, uploadQueueItem],
  );

  const handleFilesSelect = useCallback(
    (
      files: File[],
      trackType: TracksControllerTracksListType,
      maxFiles: number,
    ): File[] => {
      const isSoundkit = trackType === "SOUNDKIT";
      const rejections: string[] = [];
      const valid: File[] = [];

      for (const file of files) {
        const fileIsZip = isZip(file);
        const maxSize = fileIsZip ? 2000 : 200;

        if (!isValidFileSize(file.size, maxSize)) {
          rejections.push(`${file.name} is too large.`);
        } else if (!isValidAudioFileType(file.type)) {
          rejections.push(`${file.name} is not a supported file type.`);
        } else if (isSoundkit && !fileIsZip) {
          rejections.push(`${file.name} is not a ZIP file.`);
        } else if (!isSoundkit && fileIsZip) {
          rejections.push(`${file.name} is not an audio file.`);
        } else {
          valid.push(file);
        }
      }

      const accepted = maxFiles > 0 ? valid.slice(0, maxFiles) : [];
      const overLimit = valid.length - accepted.length;
      if (overLimit > 0) {
        rejections.push(
          maxFiles > 0
            ? `Only ${maxFiles} file${maxFiles === 1 ? "" : "s"} can be uploaded right now — ${overLimit} skipped.`
            : "You have no uploads left right now.",
        );
      }

      // Long batches would otherwise produce a wall of near-identical lines.
      const shown = rejections.slice(0, 3).join(" ");
      const hidden = rejections.length - 3;
      setErrorMessage(
        rejections.length === 0
          ? undefined
          : hidden > 0
            ? `${shown} +${hidden} more skipped.`
            : shown,
      );

      return accepted;
    },
    [],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (processingTracks.length === 0) {
        return;
      }
      processingTracks.forEach((track) => {
        if (track.status === "done") {
          return;
        }
        getStatusForTrack(track.trackId);
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [processingTracks, getStatusForTrack]);

  // On mount, fetch any ongoing track processes to populate the sticky bar
  useEffect(() => {
    if (!user) {
      return;
    }

    trackProcessControllerTrackProcessOngoing().then((ongoingTracks) => {
      for (const track of ongoingTracks) {
        addTrack(track);
      }
    });
  }, [addTrack, user]);

  // ── Context value ────────────────────────────────────────────────────

  const value: AudiolabContextType = useMemo(
    () => ({
      // Creator state
      selectedCreator: resolvedCreator,
      setSelectedCreator,
      allCreators: creators,
      defaultCreator,
      isReadOnly,
      viewAllArtists,
      // Track upload state
      isUploading,
      errorMessage,
      uploadQueue,
      processingTracks,
      createTracks,
      removeTrack,
      clearFinished,
      setShowUpload,
      openUploadForCreator,
      openUploadForRelease,
      lockedUploadCreatorId,
      openUploadForMastering,
      uploadMasteringPreset,
      uploadMaxFiles,
      handleFilesSelect,
    }),
    [
      resolvedCreator,
      setSelectedCreator,
      creators,
      defaultCreator,
      isReadOnly,
      viewAllArtists,
      isUploading,
      errorMessage,
      uploadQueue,
      processingTracks,
      createTracks,
      removeTrack,
      clearFinished,
      setShowUpload,
      openUploadForCreator,
      openUploadForRelease,
      lockedUploadCreatorId,
      openUploadForMastering,
      uploadMasteringPreset,
      uploadMaxFiles,
      handleFilesSelect,
    ],
  );

  return (
    <AudiolabContext.Provider value={value}>
      {children}
      {showUpload && <UploadWizardModal onClose={() => setShowUpload(false)} />}
    </AudiolabContext.Provider>
  );
}
